import { useState, useEffect, useContext } from "react";
import { Context } from "@/lib/Context";

// Live subscriptions per socket, counted per instrument, so a second component on the same ISIN and
// currency does not subscribe again; a new socket (reconnect) starts with no counts. The MULTI server
// keeps one subscription per ISIN and currency for the whole connection, so an unsubscribe from here
// would also end the feed of subscribers outside this count (DataGrid, TableCell, the ticker tape), as
// measured on /currencies for the grid (#1285 item 10). Until they share one count, nothing here
// unsubscribes; the subscription ends with the socket.
const subscriptionCounts = new WeakMap<WebSocket, Map<string, number>>();

const subscriptionKey = (startMessage: string): string | null => {
    try {
        const message = JSON.parse(startMessage);
        // A TS request is a one-off history snapshot: it creates no subscription, and sending
        // "unsubscribe" for it would drop another component's live subscription to the same ISIN.
        return message?.type === "subscribe" ? `${message.ISIN}|${message.currency}` : null;
    } catch {
        return null;
    }
};

export const useWebSocket = (wsType: "ortexWebSocket" | "multiWebSocket", identifier: string, startMessage: any, updateData: any, dependencies: any[] = [], unsubscribe: boolean = true, condition: boolean = false, retry: boolean = false) => {
    const { multiWebSocket, ortexWebSocket } = useContext(Context);
    const [isLoading, setIsLoading] = useState<boolean>(true);

    useEffect(() => {
        const ws: WebSocket | undefined = wsType === "multiWebSocket" ? multiWebSocket : ortexWebSocket;
        if (condition || !ws) return;

        const key = subscriptionKey(startMessage);
        let counts = subscriptionCounts.get(ws);
        if (!counts) {
            counts = new Map();
            subscriptionCounts.set(ws, counts);
        }
        const count = key ? counts.get(key) || 0 : 0;
        if (key) counts.set(key, count + 1);

        const sendStart = () => ws.send(startMessage);
        let openListener = false;
        if (count === 0 || retry) {
            if (ws.readyState === 1) {
                sendStart();
            } else {
                ws.addEventListener("open", sendStart);
                openListener = true;
            }
        }
        ws.addEventListener("message", updateData);

        return () => {
            ws.removeEventListener("message", updateData);
            if (openListener) ws.removeEventListener("open", sendStart);
            if (!key) return;

            const remaining = (counts.get(key) || 1) - 1;
            if (remaining > 0) {
                counts.set(key, remaining);
                return;
            }
            // No unsubscribe yet (see the top of this file): the next component to need the key subscribes again.
            counts.delete(key);
        };
    }, [multiWebSocket, ortexWebSocket, wsType, identifier, startMessage, condition, ...dependencies]);

    useEffect(() => {
        const timer = setTimeout(() => {
            setIsLoading(false);
        }, 60000);
        return () => clearTimeout(timer);
    }, []);

    return { isLoading };
};
